import React, { useState, useEffect } from 'react';
import SelectSymbol from '../../components/SelectSymbol/SelectSymbol';
import { getAiSignalHistory } from '../../services/OrdersService';
import { DEFAULT_SYMBOL } from '../../utils/SymbolUtils';
import { formatNumber } from '../../utils/NumberUtils';

function SignalBadge({ signal }) {
    if (signal === 'BUY') return <span className='badge bg-success'>COMPRA</span>;
    if (signal === 'SELL') return <span className='badge bg-danger'>VENDA</span>;
    return <span className='badge bg-secondary'>NEUTRO</span>;
}

function OutcomeBadge({ outcome }) {
    if (!outcome) return <span className='badge bg-light text-muted border'>Pendente</span>;
    if (outcome === 'CORRECT') return <span className='badge bg-success'>Acertou</span>;
    if (outcome === 'INCORRECT') return <span className='badge bg-danger'>Errou</span>;
    return <span className='badge bg-secondary'>Neutro</span>;
}

function ConfidenceBar({ value }) {
    const pct = Math.round((parseFloat(value) || 0) * 100);
    const color = pct >= 75 ? 'bg-success' : pct >= 50 ? 'bg-warning' : 'bg-danger';
    return (
        <div className='d-flex align-items-center gap-2'>
            <div className='progress flex-grow-1' style={{ height: 6 }}>
                <div className={`progress-bar ${color}`} style={{ width: `${pct}%` }} />
            </div>
            <span style={{ fontSize: 12, minWidth: 32 }}>{pct}%</span>
        </div>
    );
}

function PriceMove({ priceAtSignal, priceAtOutcome, signal }) {
    if (!priceAtOutcome) return <span className='text-muted'>—</span>;
    const entry = parseFloat(priceAtSignal);
    const exit = parseFloat(priceAtOutcome);
    const move = ((exit - entry) / entry) * 100;
    const sign = move >= 0 ? '+' : '';
    const color = move >= 0 ? 'text-success' : 'text-danger';
    return <span className={color}>{sign}{move.toFixed(2)}%</span>;
}

function AiSignalHistory() {
    const [symbol, setSymbol] = useState(DEFAULT_SYMBOL);
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        setLoading(true);
        setSignals([]);
        getAiSignalHistory(symbol, 50)
            .then(rows => setSignals(rows || []))
            .catch(() => setSignals([]))
            .finally(() => setLoading(false));
    }, [symbol]);

    function onSymbolChange(e) {
        setSymbol(e.target.value);
        setExpanded(null);
    }

    return (
        <div className='card border-0 shadow mb-4'>
            <div className='card-header d-flex justify-content-between align-items-center flex-wrap gap-2'>
                <h6 className='mb-0'>Histórico de Sinais da IA</h6>
                <SelectSymbol symbol={symbol} onChange={onSymbolChange} disabled={false} />
            </div>

            {loading && (
                <div className='card-body text-muted py-3 text-center' style={{ fontSize: 14 }}>
                    Carregando...
                </div>
            )}

            {!loading && signals.length === 0 && (
                <div className='card-body text-muted py-3' style={{ fontSize: 14 }}>
                    Nenhum sinal registrado para {symbol}. Os sinais aparecem aqui após a automação de IA executar.
                </div>
            )}

            {!loading && signals.length > 0 && (
                <div className='table-responsive'>
                    <table className='table table-sm table-hover mb-0 align-middle'>
                        <thead className='thead-light'>
                            <tr className="align-middle">
                                <th>Data</th>
                                <th className='text-center'>Sinal</th>
                                <th style={{ minWidth: 120 }}>Confiança</th>
                                <th className='text-end'>Preço</th>
                                <th className='text-end'>Variação</th>
                                <th className='text-center'>Resultado</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {signals.map((s, i) => (
                                <React.Fragment key={s.id || i}>
                                    <tr
                                        style={{ cursor: s.reasoning ? 'pointer' : 'default' }}
                                        onClick={() => s.reasoning && setExpanded(expanded === i ? null : i)}
                                    >
                                        <td className='text-muted' style={{ fontSize: 12 }}>
                                            {new Date(s.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                                        </td>
                                        <td className='text-center'>
                                            <SignalBadge signal={s.signal} />
                                        </td>
                                        <td>
                                            <ConfidenceBar value={s.confidence} />
                                        </td>
                                        <td className='text-end' style={{ fontSize: 12 }}>
                                            {formatNumber(parseFloat(s.priceAtSignal))}
                                        </td>
                                        <td className='text-end'>
                                            <PriceMove
                                                priceAtSignal={s.priceAtSignal}
                                                priceAtOutcome={s.priceAtOutcome}
                                                signal={s.signal}
                                            />
                                        </td>
                                        <td className='text-center'>
                                            <OutcomeBadge outcome={s.outcome} />
                                        </td>
                                        <td className='text-muted' style={{ fontSize: 12 }}>
                                            {s.reasoning ? (expanded === i ? '▲' : '▼') : ''}
                                        </td>
                                    </tr>
                                    {expanded === i && s.reasoning && (
                                        <tr className="align-middle">
                                            <td colSpan={7} className='bg-light'>
                                                <div className='px-2 py-2' style={{ fontSize: 13 }}>
                                                    <div className='mb-2 text-muted'><strong>Raciocínio:</strong> {s.reasoning}</div>
                                                    <div className='d-flex gap-3 flex-wrap'>
                                                        {s.suggestedPrice && (
                                                            <span><strong>Entrada sugerida:</strong> {formatNumber(parseFloat(s.suggestedPrice))}</span>
                                                        )}
                                                        {s.stopLoss && (
                                                            <span className='text-danger'><strong>Stop Loss:</strong> {formatNumber(parseFloat(s.stopLoss))}</span>
                                                        )}
                                                        {s.takeProfit && (
                                                            <span className='text-success'><strong>Take Profit:</strong> {formatNumber(parseFloat(s.takeProfit))}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AiSignalHistory;
